/**
 * Photos and videos for messages, prepared in the browser before upload so
 * storage and delivery stay cheap: photos are re-encoded to at most 1600 px
 * (about 150-400 KB) plus a 320 px thumbnail for the conversation, with their
 * metadata (GPS, camera) dropped by the re-encode; videos are checked against
 * the limits (30 s, 50 MB) and given a still for their poster. The server
 * compresses videos to 720p afterwards (admin-backend chat-video.job).
 *
 * The same file is in web-frontend (lib/utils/chat-media.ts) -
 * keep the two in step.
 */

export const MAX_IMAGE_SIDE = 1600;
export const THUMB_SIDE = 320;
export const MAX_IMAGES = 5;
export const MAX_VIDEO_SECONDS = 30;
export const MAX_VIDEO_BYTES = 50_000_000;
export const VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm"];
/** The uploads API's per-file limits (merchant-backend chat.service UPLOAD_KINDS). */
export const MAX_IMAGE_BYTES = 1_500_000;
export const MAX_THUMB_BYTES = 200_000;
export const MAX_POSTER_BYTES = 300_000;
/** How long to wait for a video's metadata / first frame before giving up on them. */
const VIDEO_METADATA_TIMEOUT_MS = 8_000;
const VIDEO_SEEK_TIMEOUT_MS = 4_000;

export interface PreparedImage {
  image: Blob;
  thumb: Blob;
  width: number;
  height: number;
  contentType: string;
}

export interface PreparedVideo {
  video: File;
  /** null when the browser couldn't take a still (the server then makes one). */
  poster: Blob | null;
  /** 0 when the browser couldn't read the video (the server checks it). */
  width: number;
  height: number;
  durationSec: number;
}

function loadImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      // Most often a HEIC photo on a browser that can't decode it.
      reject(new Error("That photo format isn't supported. Try a JPEG or PNG."));
    };
    img.src = url;
  });
}

function draw(
  source: CanvasImageSource,
  width: number,
  height: number,
  maxSide: number
): HTMLCanvasElement {
  const scale = Math.min(1, maxSide / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("This browser can't prepare photos.");
  // White under transparent areas (a PNG), which JPEG would otherwise turn black.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
}

/** Frees a canvas's pixel memory at once (mobile Safari caps the total). */
function release(canvas: HTMLCanvasElement | null): void {
  if (!canvas) return;
  canvas.width = 0;
  canvas.height = 0;
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/** WebP where the browser can make it (smaller), otherwise JPEG. */
async function encode(
  canvas: HTMLCanvasElement,
  quality: number
): Promise<{ blob: Blob; type: string }> {
  const webp = await toBlob(canvas, "image/webp", quality);
  if (webp && webp.type === "image/webp") return { blob: webp, type: "image/webp" };
  const jpeg = await toBlob(canvas, "image/jpeg", quality);
  if (!jpeg) throw new Error("That photo could not be prepared.");
  return { blob: jpeg, type: "image/jpeg" };
}

/**
 * Draws and encodes at each (side, quality) step until the result fits `maxBytes`;
 * the canvases are released as it goes. null if even the smallest step is too big.
 */
async function encodeWithin(
  source: CanvasImageSource,
  width: number,
  height: number,
  steps: Array<[side: number, quality: number]>,
  maxBytes: number
): Promise<{ blob: Blob; type: string; width: number; height: number } | null> {
  for (const [side, quality] of steps) {
    const canvas = draw(source, width, height, side);
    try {
      const out = await encode(canvas, quality);
      if (out.blob.size <= maxBytes) {
        return { ...out, width: canvas.width, height: canvas.height };
      }
    } finally {
      release(canvas);
    }
  }
  return null;
}

/**
 * A photo, re-encoded small, with a thumbnail. Tried smaller if it comes out over the
 * upload limit. Prepare several photos one after another, not all at once (memory).
 */
export async function prepareImage(file: File): Promise<PreparedImage> {
  if (!file.type.startsWith("image/")) throw new Error("That file is not a photo.");
  const img = await loadImage(file);
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (!w || !h) throw new Error("That photo could not be read.");
  const image = await encodeWithin(
    img,
    w,
    h,
    [
      [MAX_IMAGE_SIDE, 0.75],
      [MAX_IMAGE_SIDE, 0.6],
      [1280, 0.6],
      [1024, 0.55],
    ],
    MAX_IMAGE_BYTES
  );
  if (!image) throw new Error("That photo is too large to send, even made smaller.");
  const thumb = await encodeWithin(
    img,
    w,
    h,
    [
      [THUMB_SIDE, 0.7],
      [240, 0.6],
    ],
    MAX_THUMB_BYTES
  );
  if (!thumb) throw new Error("That photo could not be prepared.");
  return {
    image: image.blob,
    thumb: thumb.blob,
    width: image.width,
    height: image.height,
    contentType: image.type,
  };
}

/** Resolves when `event` fires on the video, rejects on error or after `ms`. */
function waitFor(video: HTMLVideoElement, event: string, ms: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const done = (ok: boolean) => {
      clearTimeout(timer);
      video.removeEventListener(event, onEvent);
      video.removeEventListener("error", onError);
      if (ok) resolve();
      else reject(new Error(event));
    };
    const onEvent = () => done(true);
    const onError = () => done(false);
    const timer = setTimeout(() => done(false), ms);
    video.addEventListener(event, onEvent);
    video.addEventListener("error", onError);
  });
}

/**
 * A video, checked against the limits, with a still for its poster (taken in the browser).
 * If the browser can't read the video at all (an allowed type and size, say a codec it
 * lacks), it goes without a poster or size (zeros) and the server checks it instead.
 */
export async function prepareVideo(file: File): Promise<PreparedVideo> {
  if (!VIDEO_TYPES.includes(file.type)) throw new Error("Send videos as MP4 or MOV.");
  if (file.size > MAX_VIDEO_BYTES) throw new Error("Videos can be up to 50 MB.");
  const unread: PreparedVideo = { video: file, poster: null, width: 0, height: 0, durationSec: 0 };
  const url = URL.createObjectURL(file);
  const video = document.createElement("video");
  try {
    video.muted = true;
    video.playsInline = true;
    video.preload = "metadata";
    video.src = url;
    const metadata = waitFor(video, "loadedmetadata", VIDEO_METADATA_TIMEOUT_MS);
    video.load();
    try {
      await metadata;
    } catch {
      return unread;
    }
    // WebM often reports Infinity until fully read: treat as unknown (0).
    const durationSec = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0;
    if (durationSec > MAX_VIDEO_SECONDS + 0.5) {
      throw new Error(`Videos can be up to ${MAX_VIDEO_SECONDS} seconds.`);
    }
    const width = video.videoWidth || 0;
    const height = video.videoHeight || 0;
    let poster: Blob | null = null;
    try {
      const seeked = waitFor(video, "seeked", VIDEO_SEEK_TIMEOUT_MS);
      video.currentTime = Math.min(0.5, durationSec / 2 || 0);
      await seeked;
      // Only with a decoded frame (HAVE_CURRENT_DATA); otherwise the still is blank.
      // A JPEG (as in web-frontend), tried smaller if over the poster limit.
      if (video.readyState >= 2 && width && height) {
        for (const [side, quality] of [
          [640, 0.7],
          [480, 0.6],
        ]) {
          const canvas = draw(video, width, height, side);
          const blob = await toBlob(canvas, "image/jpeg", quality);
          release(canvas);
          if (blob && blob.size <= MAX_POSTER_BYTES) {
            poster = blob;
            break;
          }
        }
      }
    } catch {
      poster = null;
    }
    return { video: file, poster, width, height, durationSec: Math.round(durationSec) };
  } finally {
    video.removeAttribute("src");
    video.load();
    URL.revokeObjectURL(url);
  }
}

/** PUTs a file to a presigned URL (from the uploads API), with the signed headers. */
export async function putToStorage(
  upload: { url: string; headers: Record<string, string> },
  body: Blob
): Promise<void> {
  const res = await fetch(upload.url, { method: "PUT", headers: upload.headers, body });
  if (!res.ok) throw new Error("Upload failed. Try again.");
}
